import { FieldArray, Form, Formik, type FormikHelpers } from "formik";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import {
  EDL_CLE_INITIAL_VALUE,
  EDL_CLES_INITIAL_VALUE,
  edlClesValidationSchema,
  // @ts-expect-error pure js
} from "../../models/edl";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import EdlPhotoField, { type PhotoFile } from "./EdlPhotoField";
import { useToast } from "../UI/Toast/useToast";
import type { Property } from "../../models/property";
import styles from "./ClesForm.module.css";
import IconButton from "../UI/IconButton.tsx";

interface CleValues {
  nombre: string;
  description: string;
}

interface ClesFormValues {
  cles: CleValues[];
  photos_cles: PhotoFile[];
}

interface ClesFormProps {
  property: Property;
  onSubmit: (values: ClesFormValues) => Promise<void> | void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ClesForm({
  property,
  onSubmit,
  open,
  onOpenChange,
}: ClesFormProps) {
  const toast = useToast();

  async function submitHandler(
    values: ClesFormValues,
    { resetForm }: FormikHelpers<ClesFormValues>,
  ) {
    try {
      await onSubmit(sanitizeValues(values));
      resetForm();
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  const initialValues: ClesFormValues = toInitialValuesFromSchema(
    edlClesValidationSchema,
    property,
    EDL_CLES_INITIAL_VALUE,
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={edlClesValidationSchema}
      enableReinitialize={true}
      onSubmit={submitHandler}
    >
      {({ values, resetForm }) => (
        <ModalDialog
          size="md"
          open={open}
          header="Clés du logement"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) resetForm();
            onOpenChange(nextOpen);
          }}
        >
          <Form>
            <FieldArray
              name="cles"
              render={(arrayHelpers) => (
                <div className={styles.form}>
                  {values.cles.map((_cle, index) => (
                    <div key={index} className={styles.cleRow}>
                      <TextInput
                        name={`cles[${index}].nombre`}
                        placeholder="1"
                        type="number"
                        label="Quantité"
                        marginForm=""
                      />
                      <TextInput
                        name={`cles[${index}].description`}
                        className={styles.description}
                        placeholder="Clé de l'entrée"
                        label="Type de clé"
                        marginForm=""
                      />
                      <IconButton
                        type="button"
                        variant="plain"
                        color="danger"
                        icon={IconTrash}
                        label="Supprimer cette clé"
                        onClick={() => arrayHelpers.remove(index)}
                      />
                    </div>
                  ))}
                  <Button
                    variant="tertiary"
                    type="button"
                    icon={IconPlus}
                    onClick={() => arrayHelpers.push(EDL_CLE_INITIAL_VALUE)}
                  >
                    Ajouter un type de clé
                  </Button>
                </div>
              )}
            />
            <EdlPhotoField
              path="photos_cles"
              text="Ajouter des photos des clés"
              showCover={false}
            />
            <ModalDialogFooter>
              <FormikSubmitButton requiresWrite>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
